import { useState } from 'react'
import { normalizeTrustedSender, type User } from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Field, Input } from '@/components/ui'
import { useProvisionIngestAddress, useUpdateUserProfile } from '@/features/user/hooks'

/**
 * Email forwarding settings — REQUIREMENTS.md §6.1.
 *
 * Two things live here: the address receipts get forwarded to, and the senders
 * whose forwards skip the confirmation step.
 *
 * The address is treated as a secret, because it is one. Anything delivered to
 * it lands in the review queue, so it's shown deliberately rather than printed
 * across the app, and the copy button exists so it never has to be retyped.
 */
export function EmailForwardingCard({ user }: { user: User }) {
  const provision = useProvisionIngestAddress()
  const update = useUpdateUserProfile()
  const [copied, setCopied] = useState(false)
  const [newSender, setNewSender] = useState('')
  const [senderError, setSenderError] = useState<string>()

  const address = user.ingestEmailAddress
  const trusted = user.trustedSenders

  async function copyAddress() {
    if (address === null) return
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused; the address is on screen either way.
      setCopied(false)
    }
  }

  function addSender() {
    const normalized = normalizeTrustedSender(newSender)
    if (normalized === null) {
      setSenderError(
        'Enter an email address, or a whole domain written like @amazon.com.',
      )
      return
    }
    if (trusted.includes(normalized)) {
      setSenderError('Already on the list.')
      return
    }
    setSenderError(undefined)
    setNewSender('')
    update.mutate({ trustedSenders: [...trusted, normalized] })
  }

  function removeSender(entry: string) {
    update.mutate({ trustedSenders: trusted.filter((s) => s !== entry) })
  }

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Email forwarding</h2>
        <p className="mt-1 text-sm text-slate-600">
          Forward a receipt email here and it lands in your review queue, read
          and ready to file.
        </p>
      </div>

      {address === null ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            You don't have a forwarding address yet.
          </p>
          <Button
            variant="primary"
            onClick={() => provision.mutate()}
            disabled={provision.isPending}
          >
            {provision.isPending ? 'Creating…' : 'Create my forwarding address'}
          </Button>
          {provision.error !== null && (
            <ErrorNote>{provision.error.message}</ErrorNote>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-900">
              {address}
            </code>
            <Button onClick={() => void copyAddress()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          {/* Not a warning for its own sake: the address is the only thing
              standing between a public mail server and this queue. */}
          <p className="text-xs text-slate-500">
            Keep this private. Anyone who has it can put documents in your
            review queue. Set it up as a forwarding rule in your mail client
            rather than sharing it around.
          </p>
        </div>
      )}

      <div className="space-y-3 border-t border-slate-200 pt-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Trusted senders</h3>
          <p className="mt-1 text-sm text-slate-600">
            Receipts forwarded from these senders are filed straight into the
            queue. Anything else still arrives — it's flagged for a quick look
            first, never discarded.
          </p>
        </div>

        {trusted.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {trusted.map((entry) => (
              <li
                key={entry}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="min-w-0 truncate text-sm">
                  {entry.startsWith('@') ? (
                    <>
                      <span className="font-medium">{entry}</span>
                      <Badge>Whole domain</Badge>
                    </>
                  ) : (
                    entry
                  )}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => removeSender(entry)}
                  disabled={update.isPending}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1">
            <Field
              label="Add a sender"
              hint="An address, or a whole domain written @amazon.com"
              error={senderError}
            >
              <Input
                value={newSender}
                onChange={(e) => setNewSender(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSender()
                  }
                }}
                placeholder="auto-confirm@amazon.com"
                autoComplete="off"
                invalid={senderError !== undefined}
              />
            </Field>
          </div>
          <Button onClick={addSender} disabled={update.isPending}>
            Add
          </Button>
        </div>
      </div>
    </Card>
  )
}
